import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state as litState } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { sanitizeArticleHtml } from "../article-sanitizer.js";
import {
  findAuthorItArticleLink,
  findClickResizeImage,
  getArticleLinkTitle,
  getExpandingBlockId,
  type AuthorItArticleLink,
} from "../authorit-article.js";
import { requestImageDialog } from "./jonas-image-dialog.js";
import { isSbProduct } from "../scope-catalog.js";
import {
  normalizeArticleUrl,
  type NormalizedArticleUrl,
} from "../source-url.js";
import {
  applyVideoStartTime,
  convertTime,
  generateChapterLinks,
  parseVideoSourceUrl,
} from "../video-embed.js";
import type { ChatState } from "../types.js";

const FALLBACK_SUGGESTION_URL =
  "https://klant.afas.nl/aanmaken-support-customer-care/help-center-aanvraag?&utm_source=help.afas.nl&utm_medium=verbetersuggestie-insturen";
const SB_SUGGESTION_URL =
  "https://klant.afas.nl/aanmaken-support-customer-care/help-center-aanvraag-sb?&product=sb&utm_source=help.afas.nl&utm_medium=verbetersuggestie-insturen";
const PROFIT_SUGGESTION_URL =
  "https://klant.afas.nl/aanmaken-support-customer-care/help-center-aanvraag-profit";

@customElement("help-jonas-source-document")
export class HelpJonasSourceDocument extends LitElement {
  // Light DOM render: the article is declared in the document's light DOM (a
  // slot-forwarded child of `help-jonas-chat-app`), so the bundled
  // `.source-document` AuthorIt stylesheet (styles/authorit-article.css) reaches
  // it. A Shadow-DOM render here would be unreachable by the document
  // stylesheet. Host-level rules (display/contain/color/line-height + focus
  // reset) now live in that stylesheet under the `help-jonas-source-document`
  // selector; `contain: layout style` there stays load-bearing for the
  // expandingblock shadow and the shared image dialog centering.
  protected override createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  @property({ attribute: false })
  accessor html = "";

  @property({ attribute: false })
  accessor sourceUrl = "";

  // The currently rendered article. It is decoupled from the incoming `html`
  // prop so a source switch can keep the old article on screen until the new
  // one is ready, then dissolve between the two (see willUpdate/crossFadeTo).
  @litState()
  private accessor displayHtml = "";

  private displaySourceUrl = "";
  private crossFading = false;

  private resizeObserver: ResizeObserver | null = null;

  protected override firstUpdated(): void {
    const article = this.getArticleElement();
    if (!article) return;

    this.resizeObserver = new ResizeObserver(() =>
      this.applyResponsiveArticleLayout(),
    );
    this.resizeObserver.observe(article);
    this.applyResponsiveArticleLayout();
  }

  protected override willUpdate(changedProperties: PropertyValues<this>): void {
    if (!changedProperties.has("html") && !changedProperties.has("sourceUrl")) {
      return;
    }
    // The server already sanitized the article; sanitizing again in the browser
    // that renders it closes the gap between the two parsers (see article-sanitizer).
    const nextHtml = sanitizeArticleHtml(this.html);
    // Hold the current article while a switch is still loading: the app briefly
    // pushes empty html during the fetch, so ignoring it keeps the old article
    // visible until the new one arrives and can cross-fade in.
    if (!nextHtml) return;
    if (nextHtml === this.displayHtml) {
      this.displaySourceUrl = this.sourceUrl;
      return;
    }
    // First article, an already running cross-fade, or reduced motion: swap the
    // rendered content immediately without a dissolve.
    if (!this.displayHtml || this.crossFading || this.prefersReducedMotion()) {
      this.displayHtml = nextHtml;
      this.displaySourceUrl = this.sourceUrl;
      return;
    }
    // Replacing a visible article on a source switch: dissolve old out, new in.
    void this.crossFadeTo(nextHtml, this.sourceUrl);
  }

  protected override updated(changedProperties: PropertyValues<this>): void {
    if (!changedProperties.has("html") && !changedProperties.has("sourceUrl")) {
      return;
    }
    this.applyResponsiveArticleLayout();
  }

  // Cheap content swap for a source switch: opacity-only dissolve via the Web
  // Animations API. The old article fades out, the rendered content is replaced
  // while it is invisible, then the new article fades in.
  private async crossFadeTo(nextHtml: string, nextUrl: string): Promise<void> {
    this.crossFading = true;
    const previous = this.getArticleElement();
    const fadeOut = previous?.animate([{ opacity: "1" }, { opacity: "0" }], {
      duration: 130,
      easing: "ease-in",
      fill: "forwards",
    });
    if (fadeOut) await fadeOut.finished.catch(() => undefined);

    this.displayHtml = nextHtml;
    this.displaySourceUrl = nextUrl;
    await this.updateComplete;
    this.crossFading = false;

    const next = this.getArticleElement();
    if (next) {
      const fadeIn = next.animate([{ opacity: "0" }, { opacity: "1" }], {
        duration: 200,
        easing: "ease-out",
        fill: "forwards",
      });
      void fadeIn.finished
        .catch(() => undefined)
        .finally(() => {
          fadeOut?.cancel();
          fadeIn.cancel();
        });
    } else {
      fadeOut?.cancel();
    }
    this.applyResponsiveArticleLayout();
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  override disconnectedCallback(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    super.disconnectedCallback();
  }

  protected override render() {
    return html`<article
      class="source-document"
      @click=${this.handleAuthorItContentClick}
    >
      ${unsafeHTML(this.displayHtml)}
    </article>`;
  }

  private applyResponsiveArticleLayout(): void {
    const article = this.getArticleElement();
    if (!article) return;

    const containerWidth = article.clientWidth;
    if (containerWidth <= 0) return;

    this.resizeArticleTables(article, containerWidth);
    this.applyVideoBehavior(article);
    this.resizeYoutubeEmbeds(article, containerWidth);
  }

  private applyVideoBehavior(article: HTMLElement): void {
    const parsed = parseVideoSourceUrl(this.displaySourceUrl);
    if (!parsed) return;
    generateChapterLinks(article, parsed.kbId);
    if (parsed.time) applyVideoStartTime(article, convertTime(parsed.time));
  }

  private resizeArticleTables(
    article: HTMLElement,
    containerWidth: number,
  ): void {
    article
      .querySelectorAll<HTMLTableElement>("table.tableintopic")
      .forEach((table) => {
        const originalWidth =
          this.getOriginalDimension(table, "width") || table.scrollWidth;

        table.dataset.origWidth = String(originalWidth);

        if (originalWidth > containerWidth) {
          table.style.width = "100%";
          table.classList.add("tableintopic-scroll");
          return;
        }

        table.style.width = "";
        table.classList.remove("tableintopic-scroll");
      });
  }

  private resizeYoutubeEmbeds(
    article: HTMLElement,
    containerWidth: number,
  ): void {
    const minHeight = 180;

    article
      .querySelectorAll<HTMLIFrameElement>('iframe[src*="youtube.com"]')
      .forEach((iframe) => {
        const originalWidth = this.getOriginalDimension(iframe, "width") || 560;
        const originalHeight =
          this.getOriginalDimension(iframe, "height") || 315;

        iframe.dataset.origWidth = String(originalWidth);
        iframe.dataset.origHeight = String(originalHeight);

        if (iframe.closest("table")) {
          iframe.classList.remove("youtube-fs");
          iframe.style.aspectRatio = "";
          iframe.style.width = `${originalWidth}px`;
          iframe.style.height = `${originalHeight}px`;
          return;
        }

        if (originalWidth > containerWidth) {
          const responsiveHeight = Math.round(
            containerWidth * (originalHeight / originalWidth),
          );
          const finalHeight = Math.max(responsiveHeight, minHeight);

          iframe.classList.add("youtube-fs");
          iframe.style.aspectRatio = `${originalWidth} / ${originalHeight}`;
          iframe.style.width = "100%";
          iframe.style.height = `${finalHeight}px`;
          return;
        }

        iframe.classList.remove("youtube-fs");
        iframe.style.aspectRatio = "";
        iframe.style.width = `${originalWidth}px`;
        iframe.style.height = `${originalHeight}px`;
      });
  }

  private getOriginalDimension(
    element: HTMLElement,
    attribute: "height" | "width",
  ): number {
    const datasetKey = attribute === "width" ? "origWidth" : "origHeight";
    const storedValue = Number(element.dataset[datasetKey]);
    if (Number.isFinite(storedValue) && storedValue > 0) return storedValue;

    const attributeValue = Number(element.getAttribute(attribute));
    if (Number.isFinite(attributeValue) && attributeValue > 0) {
      return attributeValue;
    }

    return 0;
  }

  private handleAuthorItContentClick(event: MouseEvent): void {
    const image = findClickResizeImage(event);
    if (image) {
      event.preventDefault();
      this.openImageDialog(image);
      return;
    }

    const link = findAuthorItArticleLink(event);
    if (!link) return;

    const toggleBlockId = getExpandingBlockId(link);
    if (!toggleBlockId) return;

    event.preventDefault();
    this.toggleExpandingBlock(toggleBlockId);
  }

  private openImageDialog(image: HTMLImageElement): void {
    // Request the shared app-root dialog via a bubbling event instead of
    // embedding one here. The source-document carries `contain: layout style`
    // (set on `help-jonas-source-document` in the bundled stylesheet), which
    // would otherwise become the containing block for the dialog's
    // `position: fixed` panel and mis-center it inside the tall, scrollable
    // article instead of the viewport. The browser resolves currentSrc/src to
    // an absolute URL, so the dialog shows the same CDN-normalized image the
    // article renders.
    requestImageDialog(this, {
      src: image.currentSrc || image.src,
      alt: image.alt,
    });
  }

  private toggleExpandingBlock(id: string): void {
    const block = this.findElementById(`d${id}`);
    if (!block) return;

    const icon = this.findElementById(`i${id}`);
    const isVisible =
      block.style.display !== "none" &&
      getComputedStyle(block).display !== "none";

    block.style.display = isVisible ? "none" : "";
    icon?.classList.toggle("expandClass", !isVisible);

    if (!isVisible) {
      requestAnimationFrame(() => this.applyResponsiveArticleLayout());
    }
  }

  private findElementById(id: string): HTMLElement | null {
    return (
      Array.from(this.renderRoot.querySelectorAll<HTMLElement>("[id]")).find(
        (element) => element.id === id,
      ) ?? null
    );
  }

  private getArticleElement(): HTMLElement | null {
    return this.renderRoot.querySelector<HTMLElement>(".source-document");
  }

  getFocusTarget(): HTMLElement | null {
    return (
      this.renderRoot.querySelector<HTMLElement>(
        "[data-article-source-pane] h1",
      ) ?? this.renderRoot.querySelector<HTMLElement>("h1, h2")
    );
  }

  scrollToHash(hash: string): boolean {
    const id = getHashId(hash);
    if (!id) return false;

    const target = Array.from(
      this.renderRoot.querySelectorAll<HTMLElement>("[id], [name]"),
    ).find(
      (element) => element.id === id || element.getAttribute("name") === id,
    );
    if (!target) return false;

    setProgrammaticFocusTarget(target);
    target.scrollIntoView({ block: "start" });
    target.focus({ preventScroll: true });
    return true;
  }
}

@customElement("help-jonas-source-panel")
export class HelpJonasSourcePanel extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      height: 100%;
      width: 100%;
      min-width: 0;
      flex-direction: column;
      background: var(--color-background-900);
    }

    /* The slide-in/slide-out translates the inner surface, not the host.
       afas-designer-layout's right-sidebar is overflow:auto, so a translated
       host would expose a horizontal scrollbar; clipping the surface on the
       host keeps the motion contained. The clip is only applied while the
       animation runs so the header's dropdown menu is never clipped at rest. */
    :host(.source-animating) {
      overflow: hidden;
    }

    .panel-surface {
      display: flex;
      flex: 1 1 auto;
      min-width: 0;
      min-height: 0;
      flex-direction: column;
    }

    .body {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
      background: var(--color-base-white);
      padding: 16px 20px 24px;
      container-type: inline-size;
      scrollbar-width: thin;
      scrollbar-color: var(--color-black-400) transparent;
    }

    .body::-webkit-scrollbar {
      width: 6px;
      height: 6px;
      background-color: transparent;
    }

    .body::-webkit-scrollbar-track {
      background-color: transparent;
    }

    .body::-webkit-scrollbar-thumb {
      background-color: var(--color-black-400);
      background-clip: padding-box;
      border-radius: 4px;
    }

    .body::-webkit-scrollbar-thumb:hover {
      background-color: var(--color-black-500);
    }

    .source-header {
      flex: 0 0 48px;
      min-height: 48px;
      max-height: 48px;
      box-sizing: border-box;
      border-bottom: 1px solid var(--color-foreground-300);
      overflow: visible;
    }

    .source-back {
      flex: 0 0 auto;
    }

    .source-title {
      flex: 1 1 auto;
      min-width: 0;
      contain: inline-size;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 600;
    }

    .source-more-actions {
      flex: 0 0 auto;
    }

    .source-more-actions-menu {
      max-width: min(320px, calc(100vw - 24px));
    }

    .source-status {
      display: grid;
      gap: 12px;
      color: var(--color-foreground-700);
    }

    .source-header-actions {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      flex: 0 0 auto;
    }

    .source-layout {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 20px;
      max-width: 100%;
    }

    /* Kept in the DOM while loading/error so the forwarded document slot never
       leaves afas-designer-layout's right-sidebar slot (which would collapse
       the panel on a source switch); only visually hidden behind the skeleton. */
    .source-layout[hidden] {
      display: none;
    }

    .source-unavailable {
      display: grid;
      gap: 4px;
    }

    .source-unavailable__title {
      font-weight: 600;
    }

    @media (max-width: 800px) {
      :host {
        position: fixed;
        inset: 0;
        z-index: 30;
      }
    }
  `;

  @property({ attribute: false })
  accessor state: ChatState | null = null;

  @property({ type: Boolean, attribute: "can-go-back" })
  accessor canGoBack = false;

  @property({ type: Number, attribute: "scroll-top-offset" })
  accessor scrollTopOffset = 0;

  @litState()
  private accessor copiedSourceUrl = "";

  private lastFocusedSourceKey = "";
  // Becomes true once an article has been shown for the current open session
  // (reset when the panel closes). It distinguishes a first load (show the
  // skeleton) from a source switch (keep the old article for the cross-fade).
  private hasShownSourceContent = false;

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("keydown", this.handleWindowKeydown);
  }

  override disconnectedCallback(): void {
    window.removeEventListener("keydown", this.handleWindowKeydown);
    super.disconnectedCallback();
  }

  // Escape closes the open source panel. The image dialog registers a
  // capture-phase Escape listener that stops propagation while it is open, so
  // this bubble-phase handler only fires once the image viewer is gone. The
  // activeSource guard keeps it inert whenever no source is shown.
  private readonly handleWindowKeydown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || event.defaultPrevented) return;
    if (!this.state?.activeSource) return;
    event.preventDefault();
    this.closePanel();
  };

  protected override willUpdate(): void {
    const source = this.state?.activeSource;
    if (!source) {
      this.hasShownSourceContent = false;
    } else if (!source.loading && source.html) {
      this.hasShownSourceContent = true;
    }
  }

  protected override updated(): void {
    void this.focusBodyAfterLoad();
  }

  protected override render() {
    const source = this.state?.activeSource;
    if (!source) return null;

    // A source switch keeps the panel and the old article on screen; only a
    // first load shows the skeleton. During a switch the layout stays visible
    // so the source-document can cross-fade the old article into the new one.
    const isSwitching = source.loading && this.hasShownSourceContent;

    return html`
      <div class="panel-surface">
        <afas-container-header class="source-header">
          ${this.canGoBack
            ? html`<afas-button
                slot="prefix"
                class="source-back"
                variant="minimal"
                tone="neutral"
                icon-only
                label=${this.getSourceLabel("sourceBack", "Vorige")}
                tooltip=${this.getSourceLabel("sourceBack", "Vorige")}
                @click=${() => this.goBackInSource()}
              >
                <afas-icon slot="icon" icon="ArrowBackOutlined"></afas-icon>
              </afas-button>`
            : nothing}
          <span class="source-title" title=${source.title}
            >${source.title}</span
          >
          <div slot="suffix" class="source-header-actions">
            ${this.renderMoreMenu(source)}
            <afas-button
              variant="minimal"
              tone="neutral"
              icon-only
              label=${this.getSourceLabel("sourceCloseAriaLabel", "Sluit bron")}
              @click=${() => this.closePanel()}
            >
              <afas-icon slot="icon" icon="CloseOutlined"></afas-icon>
            </afas-button>
          </div>
        </afas-container-header>
        <div
          class="body"
          aria-busy=${source.loading ? "true" : "false"}
          @click=${this.handleContentClick}
        >
          ${source.loading && !isSwitching
            ? html`<div class="source-status" role="status" aria-live="polite">
                <afas-skeleton></afas-skeleton>
                <span>
                  ${this.getSourceLabel("sourceLoading", "Bron wordt geladen")}
                </span>
              </div>`
            : null}
          ${source.error
            ? html`<afas-alert type="danger">
                <span class="source-unavailable">
                  <span class="source-unavailable__title">
                    ${this.getSourceLabel(
                      "sourceUnavailableTitle",
                      "Bron niet beschikbaar",
                    )}
                  </span>
                  <span>
                    ${this.getSourceLabel(
                      "sourceUnavailableMessage",
                      "De bron kan niet worden geladen.",
                    )}
                  </span>
                </span>
              </afas-alert>`
            : null}
          <div
            class="source-layout"
            ?hidden=${(source.loading && !isSwitching) || Boolean(source.error)}
          >
            <slot name="document"></slot>
          </div>
        </div>
      </div>
    `;
  }

  private renderMoreMenu(source: NonNullable<ChatState["activeSource"]>) {
    const articleUrl = this.getSourceArticleUrl(source);
    const copyLabel =
      this.copiedSourceUrl === articleUrl
        ? this.getSourceLabel("sourceCopyArticleLinkCopied", "Link gekopieerd")
        : this.getSourceLabel(
            "sourceCopyArticleLink",
            "Kopieer de link van het artikel",
          );
    const suggestLabel = this.getSourceLabel(
      "sourceSuggestImprovement",
      "Verbetersuggestie voor dit artikel",
    );

    return html`
      <afas-dropdown-button
        class="source-more-actions"
        variant="default"
        tone="neutral"
        size="medium"
        label=${this.getSourceLabel("sourceMoreActions", "Meer")}
        tooltip=${this.getSourceLabel(
          "sourceMoreActionsTooltip",
          "Toon meer acties",
        )}
        placement="bottom-end"
        testid="source-more-actions"
      >
        <afas-icon slot="icon" icon="MoreHorizontalOutlined"></afas-icon>
        <afas-menu
          class="source-more-actions-menu"
          aria-label=${this.getSourceLabel(
            "sourceActionsLabel",
            "Artikelacties",
          )}
        >
          <afas-menu-item
            label=${copyLabel}
            @click=${() => this.copySourceArticleLink(source)}
          >
            <afas-icon
              slot="icon"
              icon="ContentCopyOutlined"
              size="small"
            ></afas-icon>
            ${copyLabel}
          </afas-menu-item>
          <afas-menu-divider></afas-menu-divider>
          <afas-menu-item
            label=${suggestLabel}
            @click=${() => this.openSuggestImprovement()}
          >
            <afas-icon slot="icon" icon="ChatOutlined" size="small"></afas-icon>
            ${suggestLabel}
          </afas-menu-item>
        </afas-menu>
      </afas-dropdown-button>
    `;
  }

  private closePanel(): void {
    void this.playExit().then(() => {
      this.dispatchEvent(
        new CustomEvent("jonas-source-close", {
          bubbles: true,
          composed: true,
        }),
      );
    });
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  // Slide + fade the panel surface in from the right edge. Called by the shell
  // only for a live, user-initiated source open; refresh/SSR, history restore,
  // and source switches mount the panel without animating. Honors
  // prefers-reduced-motion by appearing instantly.
  async playEntrance(): Promise<void> {
    if (this.prefersReducedMotion()) return;
    await this.updateComplete;
    const surface =
      this.renderRoot.querySelector<HTMLElement>(".panel-surface");
    if (!surface) return;
    this.classList.add("source-animating");
    const animation = surface.animate(
      [
        { transform: "translateX(100%)", opacity: "0" },
        { transform: "translateX(0)", opacity: "1" },
      ],
      { duration: 260, easing: "ease-out" },
    );
    try {
      await animation.finished;
    } catch {
      // A superseded/cancelled entrance just leaves the surface in place.
    } finally {
      this.classList.remove("source-animating");
    }
  }

  // Slide + fade the panel surface out to the right edge before the panel is
  // unmounted. Only the close button / Escape route through here; browser
  // history navigation closes the source without animating. The forwards fill
  // keeps the surface hidden until the unmount so it never snaps back.
  private async playExit(): Promise<void> {
    if (this.prefersReducedMotion()) return;
    const surface =
      this.renderRoot.querySelector<HTMLElement>(".panel-surface");
    if (!surface) return;
    this.classList.add("source-animating");
    const animation = surface.animate(
      [
        { transform: "translateX(0)", opacity: "1" },
        { transform: "translateX(100%)", opacity: "0" },
      ],
      { duration: 220, easing: "ease-in", fill: "forwards" },
    );
    try {
      await animation.finished;
    } catch {
      // A cancelled exit falls through and closes anyway.
    }
  }

  private goBackInSource(): void {
    if (!this.canGoBack) return;
    this.dispatchEvent(
      new CustomEvent("jonas-source-back", { bubbles: true, composed: true }),
    );
  }

  private async copySourceArticleLink(
    source: NonNullable<ChatState["activeSource"]>,
  ): Promise<void> {
    const articleUrl = this.getSourceArticleUrl(source);
    await navigator.clipboard.writeText(articleUrl);
    this.copiedSourceUrl = articleUrl;
  }

  private openSuggestImprovement(): void {
    window.open(this.getSuggestImprovementUrl(), "_blank", "noopener");
  }

  private getSourceArticleUrl(
    source: NonNullable<ChatState["activeSource"]>,
  ): string {
    const target = new URL(source.url, window.location.href);
    const currentUrl = new URL(window.location.href);
    const currentDomain = currentUrl.searchParams.get("domain");
    if (currentDomain && isLocalHost(target)) {
      target.searchParams.set("domain", currentDomain);
    }
    return target.toString();
  }

  private getSuggestImprovementUrl(): string {
    const product = this.getCurrentProduct();
    if (!product) return FALLBACK_SUGGESTION_URL;

    if (isSbProduct(product)) {
      return SB_SUGGESTION_URL;
    }

    const parameters = new URLSearchParams({
      product,
      workarea: this.getCurrentWorkArea(),
      utm_source: "help.afas.nl",
      utm_medium: "verbetersuggestie-insturen",
    });
    return `${PROFIT_SUGGESTION_URL}?${parameters.toString()}`;
  }

  private getCurrentProduct(): string {
    return (
      this.state?.context.product ||
      this.state?.scope.product?.current.value ||
      ""
    )
      .trim()
      .toLowerCase();
  }

  private getCurrentWorkArea(): string {
    const filters = this.state?.context.filters ?? [];
    const workAreaFilter = filters.find((filter) => {
      const name = filter.name.trim().toLowerCase();
      return (
        name === "werkgebied" || name === "workarea" || name === "work area"
      );
    });
    return workAreaFilter?.value.split(",")[0]?.trim() ?? "";
  }

  private handleContentClick(event: MouseEvent): void {
    const link = findAuthorItArticleLink(event);
    if (!link) return;
    // A link whose resolved scheme is not web, mail or phone is script, whatever modifier
    // or target the click carries: it must not reach the browser's default action either.
    if (!isNavigableProtocol(link.protocol)) {
      event.preventDefault();
      return;
    }
    if (!shouldConsiderLink(event, link)) return;

    const href = link.getAttribute("href") ?? "";
    const articleUrl = normalizeArticleUrl(href, this.getSourceLinkBaseUrl());
    if (!articleUrl) return;

    if (this.isCurrentSourceAnchor(articleUrl)) {
      event.preventDefault();
      this.scrollCurrentSourceAnchor(articleUrl);
      this.dispatchEvent(
        new CustomEvent("jonas-source-anchor", {
          detail: { url: articleUrl.path },
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }

    event.preventDefault();
    this.dispatchEvent(
      new CustomEvent("jonas-source-open", {
        detail: {
          url: articleUrl.path,
          title: getArticleLinkTitle(link, articleUrl.path),
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private getSourceLinkBaseUrl(): string {
    const sourceUrl = this.state?.activeSource?.url;
    if (!sourceUrl) return window.location.href;
    return new URL(sourceUrl, window.location.href).href;
  }

  private isCurrentSourceAnchor(articleUrl: NormalizedArticleUrl): boolean {
    const currentSourceUrl = this.state?.activeSource?.url;
    if (!currentSourceUrl) return false;
    const currentArticleUrl = normalizeArticleUrl(
      currentSourceUrl,
      window.location.href,
    );
    if (!currentArticleUrl) return false;
    return stripHash(articleUrl.path) === stripHash(currentArticleUrl.path);
  }

  // The slot-forwarded article lives in the document light DOM, so it is not in
  // this panel's shadow root. Resolve it through the panel's document slot,
  // flattening past the shell's forwarding slot to the real element.
  private getSlottedSourceDocument(): HelpJonasSourceDocument | null {
    const slot = this.renderRoot.querySelector<HTMLSlotElement>(
      'slot[name="document"]',
    );
    if (!slot) return null;
    for (const element of slot.assignedElements({ flatten: true })) {
      if (element instanceof HelpJonasSourceDocument) return element;
    }
    return null;
  }

  private scrollCurrentSourceAnchor(articleUrl: NormalizedArticleUrl): boolean {
    const hash = new URL(articleUrl.href).hash;
    return this.getSlottedSourceDocument()?.scrollToHash(hash) ?? false;
  }

  private async focusBodyAfterLoad(): Promise<void> {
    const source = this.state?.activeSource;
    if (!source || source.loading) {
      this.lastFocusedSourceKey = "";
      return;
    }

    const focusState = source.error ? "error" : source.html ? "loaded" : "";
    if (!focusState) return;

    const focusKey = `${source.url}|${focusState}`;
    if (this.lastFocusedSourceKey === focusKey) return;
    this.lastFocusedSourceKey = focusKey;

    const sourceDocument = this.getSlottedSourceDocument();
    if (sourceDocument) await sourceDocument.updateComplete;
    if (this.state?.activeSource?.url !== source.url) return;

    const body = this.shadowRoot?.querySelector<HTMLElement>(".body");
    let scrolledToHash = false;

    if (
      body &&
      typeof this.scrollTopOffset === "number" &&
      this.scrollTopOffset > 0
    ) {
      body.scrollTop = this.scrollTopOffset;
    } else if (sourceDocument) {
      const hashIndex = source.url.indexOf("#");
      const hash =
        hashIndex !== -1 ? source.url.slice(hashIndex) : window.location.hash;
      if (hash) {
        scrolledToHash = sourceDocument.scrollToHash(hash);
      }
      // A fresh source open (no saved back/forward offset and no in-page hash) must
      // start at the top. The .body scroll container is reused across source swaps, so
      // without this it keeps the previous article's scroll position (issue #116:
      // opening a second theme in the panel stayed scrolled to the first theme's level).
      if (!scrolledToHash && body) {
        body.scrollTop = 0;
      }
    }

    if (!scrolledToHash) {
      const focusTarget = this.getSourceFocusTarget(Boolean(source.error));
      if (focusTarget) {
        setProgrammaticFocusTarget(focusTarget);
        focusTarget.focus({ preventScroll: true });
      }
    }
  }

  private getSourceFocusTarget(hasError: boolean): HTMLElement | null {
    const body = this.shadowRoot?.querySelector<HTMLElement>(".body");
    if (!body) return null;

    return (
      this.getSlottedSourceDocument()?.getFocusTarget() ??
      (hasError ? body.querySelector<HTMLElement>("afas-alert") : null)
    );
  }

  private getSourceLabel(key: string, fallback: string): string {
    const value = this.state?.labels?.[key]?.trim();
    return value || fallback;
  }
}

function setProgrammaticFocusTarget(element: HTMLElement): void {
  if (!element.hasAttribute("tabindex")) {
    element.setAttribute("tabindex", "-1");
  }
}

// Schemes the browser may follow natively from an article: web links go through the panel,
// mail and phone links open their handler.
const NAVIGABLE_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

function isNavigableProtocol(protocol: string): boolean {
  return NAVIGABLE_PROTOCOLS.has(protocol.toLowerCase());
}

function shouldConsiderLink(
  event: MouseEvent,
  link: AuthorItArticleLink,
): boolean {
  if (event.defaultPrevented || event.button !== 0) return false;
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
    return false;

  const target = link.target.trim().toLowerCase();
  if (target && target !== "_self" && target !== "_blank") return false;

  const href = (link.getAttribute("href") ?? "").trim();
  if (!href) return false;

  // The resolved protocol, not the raw attribute: the browser decodes entities and drops
  // tabs and newlines before it parses a URL, so "&#106;avascript:" is "javascript:" to it.
  const protocol = link.protocol.toLowerCase();
  return protocol === "http:" || protocol === "https:";
}

function getHashId(hash: string): string {
  const value = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function stripHash(value: string): string {
  const hashIndex = value.indexOf("#");
  return hashIndex === -1 ? value : value.slice(0, hashIndex);
}

function isLocalHost(url: URL): boolean {
  return url.hostname === "localhost" || url.hostname === "127.0.0.1";
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-source-document": HelpJonasSourceDocument;
    "help-jonas-source-panel": HelpJonasSourcePanel;
  }
}
